import { UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AssetDto, GalleryDto, Section } from '@picflow/types';
import { BillingScope, billingScopes } from '../../../billing-consts';
import { debounce } from '../../../lib/debounce';
import { AnalyticsService } from '../../../services/analytics.service';
import { PermissionService } from '../../../services/permission.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { ProTagComponent } from '../../../shared-extra/components/pro-tag/pro-tag.component';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { InputPreDirective } from '../../../shared-extra/directives/input-pre.directive';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-advanced-mode-content-header',
  templateUrl: './advanced-mode-content-header.component.html',
  styleUrls: ['./advanced-mode-content-header.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeaderComponent,
    ButtonDirective,
    SvgComponent,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    InputPreDirective,
    InputPostDirective,
    ResizeDirective,
    ContextMenuComponent,
    ProTagComponent,
    RouterLink,
    UpperCasePipe,
    BillingScopePipe,
    TranslatePipe,
  ],
})
export class AdvancedModeContentHeaderComponent implements OnInit {
  private permissionService = inject(PermissionService);
  private analytics = inject(AnalyticsService);
  @Output() onHeaderToggle = new EventEmitter();
  @Output() onSearch = new EventEmitter();
  @Output() onSortingAndGroupingChange = new EventEmitter();
  @Output() onAssetSizeChange = new EventEmitter();
  @Input() clientMode: boolean;
  @Input() showSidebar: boolean;
  @Input() assets: AssetDto[];
  @Input() gallery: GalleryDto;
  @Input() sections: Section[];
  @Input() sortingAndGrouping: string;
  @Input() assetSize: number;
  @Input() assetMinSize: number;
  @Input() assetMaxSize: number;
  @Input() allowSidebar: boolean;
  @Input() isGrouping: boolean;
  exactMatch = false;

  @ViewChild('searchField') searchField: ElementRef;
  groups = {
    likes: false,
    auto: false,
    status: false,
    color_label: false,
  };

  form = new FormGroup({
    searchTerm: new FormControl(),
  });

  sortingAndGroupings: { key: string; allowFlip: boolean; scope?: BillingScope }[] = [
    { key: 'auto', allowFlip: false },
    { key: 'asset_name', allowFlip: true },
    { key: 'created_at', allowFlip: true },
    { key: 'likes', allowFlip: false },
    { key: 'status', allowFlip: false, scope: billingScopes.asset.status },
    { key: 'color_label', allowFlip: false, scope: billingScopes.asset.colorLabels },
  ];

  ngOnInit() {
    if (this.isGrouping) this.groups[this.sortingAndGrouping] = true;
  }

  toggleHeader() {
    this.onHeaderToggle.emit();
  }

  setSearch(searchTerm: string) {
    this.form.setValue({ searchTerm });
    this.search();
  }

  search() {
    this.onSearch.emit([this.form.value.searchTerm, this.exactMatch]);
  }

  toggleExact() {
    this.exactMatch = !this.exactMatch;
    this.search();
  }

  changeSortingAndGrouping(sort: string) {
    const sortingAndGrouping = this.sortingAndGroupings.find(s => s.key === sort);
    if (sortingAndGrouping.scope && !this.permissionService.canDisplayElement(sortingAndGrouping.scope)) {
      return;
    }
    const same = sort.split('-').join('') === this.sortingAndGrouping.split('-').join('');
    if (same) return;
    this.onSortingAndGroupingChange.emit(sort);
  }

  flipSort(sort: string) {
    if (!this.sortingAndGrouping.startsWith('-')) sort = '-' + sort;
    this.onSortingAndGroupingChange.emit(sort);
  }

  changeAssetSize() {
    this.onAssetSizeChange.emit(this.assetSize);
    this.changeAssetSizeDebounced();
  }

  changeAssetSizeDebounced = debounce(() => {
    this.analytics.track('am: change-asset-size');
  }, 1000);

  // showKeyboardShortcutsDialog() {
  //   this.dialog.open(KeyboardShortcutsMenuComponent, {});
  // }

  public focusSearch() {
    this.searchField.nativeElement.focus();
  }

  public unfocusSearch() {
    this.searchField.nativeElement.blur();
  }
}
